import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/atom-one-dark.css';

interface MarkdownMessageProps {
  content: string;
}

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ content }) => {
  return (
    <div className="markdown-message">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        children={content}
        components={{
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--obsidian-accent)', textDecoration: 'underline' }}
              {...props}
            >
              {children}
            </a>
          ),
          code: ({ className, children, ...props }) => {
            const isInline = !className;
            if (isInline) {
              return (
                <code
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '0.9em',
                    fontFamily: 'ui-monospace, "Cascadia Code", "Fira Code", monospace',
                  }}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children, ...props }) => (
            <pre
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                borderRadius: '8px',
                padding: '12px 16px',
                overflowX: 'auto',
                fontSize: '0.9em',
                fontFamily: 'ui-monospace, "Cascadia Code", "Fira Code", monospace',
                border: '1px solid var(--obsidian-border)',
                margin: '8px 0',
              }}
              {...props}
            >
              {children}
            </pre>
          ),
          table: ({ children, ...props }) => (
            <div style={{ overflowX: 'auto', margin: '8px 0' }}>
              <table
                style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  fontSize: '0.9em',
                }}
                {...props}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children, ...props }) => (
            <th
              style={{
                border: '1px solid var(--obsidian-border)',
                padding: '8px 12px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                textAlign: 'left',
                fontWeight: 600,
              }}
              {...props}
            >
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td
              style={{
                border: '1px solid var(--obsidian-border)',
                padding: '8px 12px',
              }}
              {...props}
            >
              {children}
            </td>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote
              style={{
                borderLeft: '3px solid var(--obsidian-accent)',
                margin: '8px 0',
                padding: '4px 16px',
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
                borderRadius: '0 6px 6px 0',
              }}
              {...props}
            >
              {children}
            </blockquote>
          ),
          ul: ({ children, ...props }) => (
            <ul style={{ paddingLeft: '20px', margin: '4px 0' }} {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol style={{ paddingLeft: '20px', margin: '4px 0' }} {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li style={{ marginBottom: '2px' }} {...props}>
              {children}
            </li>
          ),
          h1: ({ children, ...props }) => (
            <h1 style={{ fontSize: '1.4em', fontWeight: 700, margin: '12px 0 6px', color: 'var(--obsidian-accent)' }} {...props}>{children}</h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 style={{ fontSize: '1.2em', fontWeight: 600, margin: '10px 0 4px', color: 'var(--obsidian-accent)' }} {...props}>{children}</h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 style={{ fontSize: '1.1em', fontWeight: 600, margin: '8px 0 4px' }} {...props}>{children}</h3>
          ),
          hr: ({ ...props }) => (
            <hr style={{ border: 'none', borderTop: '1px solid var(--obsidian-border)', margin: '12px 0' }} {...props} />
          ),
          p: ({ children, ...props }) => (
            <p style={{ margin: '4px 0' }} {...props}>{children}</p>
          ),
        }}
      />
    </div>
  );
};

export default MarkdownMessage;
